import {
  Box,
  ButtonBase,
  Card,
  CardContent,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import React, { FC, useMemo } from 'react'
import { ItemImageArea } from 'utoniq-ui'

import {
  Image,
  ItemOrder,
  ItemOrdersConnectionFragment,
  ItemSerial,
  Maybe,
  Resource,
  SkuUnit,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { ScrollFetcher } from '@/components/organisms/functionals'
import { useRouter, useTranslation } from '@/core'
import { Template } from '@/core/architecture'
import { encodeSkuId } from '@/core/sku'
export * from './fragments'

export type State = {
  itemOrders: Maybe<ItemOrdersConnectionFragment>
  loading: boolean
  hasNextPage: boolean
}

type ItemOrdersListBoxProps = {
  item: Pick<ItemOrder, 'id' | 'formattedCreatedAt' | 'price'> & {
    item?: Maybe<{
      id?: string
      title: string
      thumbnail: { front?: Maybe<Pick<Resource, 'uri' | 'smallUri'>> }
    }>
    serial?: Maybe<
      Pick<ItemSerial, 'id' | 'skuId'> & {
        sku?: Maybe<
          Pick<SkuUnit, 'id'> &
            Partial<Pick<SkuUnit, 'unitName'>> & {
              thumbnail?: Maybe<Pick<Image, 'uri' | 'smallUri'>>
            }
        >
      }
    >
  }
}

type ItemListBoxSkeletonProps = {
  fullWidth?: boolean
}

export type Action = {
  onFetchMore: () => Promise<void>
}

export const ItemOrdersTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
}) => {
  const { t } = useTranslation('itemOrders')
  const { routerNames, routerPages } = useRouter()
  //state.itemOrdersのserialsを一つずつ取り出して新しいitemOrdersを作成
  const expandedItemOrders = useMemo(
    () =>
      state.itemOrders?.edges
        ?.filter(edge => edge?.node)
        .flatMap(edge => {
          // 重複を削除したserials配列を作成
          const uniqueSerials = (edge.node.serials ?? []).filter(
            (serial, index, self) =>
              index === self.findIndex(s => s.skuId === serial.skuId)
          )
          // 重複を削除したserialsから新しいitemOrdersを作成
          return uniqueSerials.map(serial => ({
            id: edge.node.id,
            formattedCreatedAt: edge.node.formattedCreatedAt,
            price: edge.node.price,
            item: edge.node.item,
            serial: serial,
          }))
        }) ?? [],
    [state.itemOrders]
  )
  return (
    <BaseLayout
      margin
      header={{
        back: true,
        defaultBackPage: routerPages.Management,
        title: t('title'),
      }}
    >
      <Head title={routerNames.ItemOrders} />
      <ScrollFetcher
        nodes={expandedItemOrders.map(item => (
          <ItemOrdersListBox
            key={`${item.id}-${item.serial?.id}`}
            item={item}
          />
        ))}
        onFetchMore={onFetchMore}
        loading={state.loading}
        hasNextPage={state.hasNextPage}
        componentProps={{ spacing: 5 }}
        loadingComponent={<ItemListBoxSkeleton fullWidth />}
        emptyMessage={t('noItem')}
      />
    </BaseLayout>
  )
}

const ItemOrdersListBox = ({
  item: { item, formattedCreatedAt, serial },
}: ItemOrdersListBoxProps) => {
  const { router, routerPages, generatePath } = useRouter()
  return (
    <Card
      sx={{
        mt: 2,
        borderRadius: 2,
      }}
    >
      <Stack flexDirection="row" alignItems="stretch" sx={{ p: 5 }}>
        <CardContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            flex: 1,
            p: 0,
          }}
        >
          <Stack spacing={1}>
            <Typography variant="subtitle1">{item?.title}</Typography>
            {!!serial?.sku?.unitName && (
              <Typography variant="caption">{serial?.sku?.unitName}</Typography>
            )}
            <Typography variant="body2">{formattedCreatedAt}</Typography>
          </Stack>
          <Box>
            {/* @todo: point doesn't have api yet */}
            {/*{point && <PointDisplay point={100} size="medium" />}*/}
            {/**
             * @description
             * Open when function is implemented in the future
             * https://app.clickup.com/t/908127/UT-5632
             */}
            {/* Open when function is implemented in the future */}
            {/* https://app.clickup.com/t/908127/UT-5632 */}
            {/*{price !== null && (*/}
            {/*  <EmphasizedNumber variant="h5">¥ {price}</EmphasizedNumber>*/}
            {/*)}*/}
          </Box>
        </CardContent>
        <ButtonBase
          onClick={() => {
            router.push({
              pathname: generatePath(routerPages.OwnedItemDetail, {
                itemId: item?.id ?? '#',
              }),
              search: serial?.skuId
                ? `?skuId=${encodeSkuId(serial.skuId)}`
                : '',
            })
          }}
        >
          <ItemImageArea
            size="small"
            src={
              serial?.sku?.thumbnail?.smallUri ??
              item?.thumbnail?.front?.smallUri ??
              ''
            }
          />
        </ButtonBase>
      </Stack>
    </Card>
  )
}

const ItemListBoxSkeleton: FC<ItemListBoxSkeletonProps> = ({ fullWidth }) => {
  return (
    <Stack sx={{ py: 2 }}>
      {[1, 2, 3, 4, 5].map(i => (
        <Skeleton
          key={i}
          variant="rectangular"
          sx={{
            width: fullWidth ? '100%' : 335,
            height: 136,
            borderRadius: 2,
            mb: 5,
          }}
        />
      ))}
    </Stack>
  )
}
